import { BaseQueryFn } from '@reduxjs/toolkit/query';
// import { addInterceptors } from '../services/Service';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { Auth0ContextInterface } from '@auth0/auth0-react';
import {
  getAuthToken,
  redirectToLogout,
  redirectToV2Logout,
} from './authUtils';
import LocalAuth from '@shamrock-core/common/authentication/local-authentication';

interface Auth0Context {
  getAccessTokenSilently?: Auth0ContextInterface['getAccessTokenSilently'];
  isAuthenticated?: boolean;
}

const auth0Context: Auth0Context = {
  getAccessTokenSilently: undefined,
  isAuthenticated: false,
};

export const axiosBaseQuery = (
  { baseUrl }: { baseUrl: string } = { baseUrl: '' }
): BaseQueryFn<
  {
    url: string;
    method: AxiosRequestConfig['method'];
    data?: AxiosRequestConfig['data'];
    params?: AxiosRequestConfig['params'];
  },
  unknown,
  unknown
> => {
  const axiosInstance = axios.create({ baseURL: baseUrl });
  axiosInstance.interceptors.request.use(
    async (config) => {
      const token = await getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      }
      if (auth0Context.getAccessTokenSilently) {
        try {
          const token = await auth0Context.getAccessTokenSilently();
          config.headers.Authorization = `Bearer ${token}`;
          return config;
        } catch (error) {
          console.log('Error getting token:', error);
          axios.interceptors.response.use(
            (response) => response,
            async (error) => {
              if (
                error?.response?.status === 401 ||
                error?.response?.status === 403
              ) {
                LocalAuth.clearStorage();
                if (auth0Context.isAuthenticated) {
                  redirectToV2Logout();
                } else {
                  redirectToLogout();
                }
              }
              return Promise.reject(error);
            }
          );
        }
      }

      return config;
    },
    (error: AxiosError): Promise<AxiosError> => Promise.reject(error)
  );

  return async ({ url, method, data, params }) => {
    try {
      const result = await axiosInstance({
        url,
        method,
        data,
        params,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;

      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };
};
